// Google Analytics utility functions
// Tracks custom events and user interactions

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}

/**
 * Track a page view
 * @param url - The page URL or path
 * @param title - Optional page title
 */
export const trackPageView = (url: string, title?: string) => {
  if (typeof window.gtag === 'function') {
    window.gtag('config', 'G-LDH78MK6XJ', {
      page_path: url,
      page_title: title
    });
  }
};

/**
 * Track a custom event
 * @param eventName - Name of the event (e.g., 'button_click', 'match_scored')
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track organization selection
 * @param orgId - Organization ID
 * @param orgName - Organization name
 */
export const trackOrganizationView = (orgId: string, orgName: string) => {
  trackEvent('view_organization', {
    organization_id: orgId,
    organization_name: orgName
  });
};

/**
 * Track player registration
 * @param division - Division name
 */
export const trackPlayerRegistration = (division: string) => {
  trackEvent('player_registration', {
    division: division
  });
};

/**
 * Track match scoring
 * @param matchType - Type of match (e.g., 'league_day', 'tournament')
 */
export const trackMatchScored = (matchType: string) => {
  trackEvent('match_scored', {
    match_type: matchType
  });
};

/**
 * Track button/feature usage
 * @param featureName - Name of the feature
 * @param action - Action performed
 */
export const trackFeatureUsage = (featureName: string, action: string) => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action: action
  });
};

/**
 * Track user engagement time on a page
 * @param pageName - Name of the page
 * @param timeInSeconds - Time spent on page in seconds
 */
export const trackEngagementTime = (pageName: string, timeInSeconds: number) => {
  trackEvent('user_engagement', {
    page_name: pageName,
    engagement_time_msec: timeInSeconds * 1000
  });
};

/**
 * Track Nations League interactions
 * @param action - Action performed (e.g., 'view_standings', 'view_match')
 */
export const trackNationsLeagueEvent = (action: string) => {
  trackEvent('nations_league_interaction', {
    action: action
  });
};

/**
 * Track tournament interactions
 * @param action - Action performed
 * @param tournamentName - Optional tournament name
 */
export const trackTournamentEvent = (action: string, tournamentName?: string) => {
  trackEvent('tournament_interaction', {
    action: action,
    tournament_name: tournamentName
  });
};

export default {
  trackPageView,
  trackEvent,
  trackOrganizationView,
  trackPlayerRegistration,
  trackMatchScored,
  trackFeatureUsage,
  trackEngagementTime,
  trackNationsLeagueEvent,
  trackTournamentEvent
};
